import { useState, useEffect, useMemo, useCallback } from "react";
import "../assets/css/tutor-list.css";
import { fetchData, getData } from "../components/firebase/firebase_methods";
import { databaseKey } from "../components/common/database_key";
import userImg from "../assets/img/signup/profile.png";
import TutorMetas from "../metas/tutor";

const Tutor = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [tutors, setTutors] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [skills, setSkills] = useState([]);
  const [cities, setCities] = useState([]);
  const [filters, setFilters] = useState({
    subject: "",
    skill: "",
    city: "",
    sortOrder: "",
  });

  useEffect(() => {
    const fetchTutors = async () => {
      try {
        const collectionName = databaseKey();
        const [
          usersSnapshot,
          tutorsSnapshot,
          citiesSnapshot,
          subjectsSnapshot,
        ] = await Promise.all([
          getData(fetchData(`${collectionName}/users`)),
          getData(fetchData(`${collectionName}/tutors`)),
          getData(fetchData("/cities")),
          getData(fetchData("/subjects")),
        ]);

        setSubjects(
          subjectsSnapshot.exists()
            ? Object.values(subjectsSnapshot.val()).map((s) => s.name)
            : []
        );
        setCities(
          citiesSnapshot.exists()
            ? Object.values(citiesSnapshot.val()).map((c) => c.name)
            : []
        );

        if (usersSnapshot.exists() && tutorsSnapshot.exists()) {
          const usersData = usersSnapshot.val();
          const tutorsData = tutorsSnapshot.val();

          const allTutors = Object.entries(usersData)
            .filter(([_, user]) => user.isTutor && user.isProfilePublic)
            .map(([id, user]) => ({ ...user, ...(tutorsData[id] || {}) }));

          setTutors(allTutors);
          setSkills([
            ...new Set(allTutors.flatMap((tutor) => tutor.skills || [])),
          ]);
        }
      } catch (error) {
        console.error("Error fetching tutors:", error);
      }
    };

    fetchTutors();
  }, []);

  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value.toLowerCase());
  }, []);

  const filteredTutors = useMemo(() => {
    let result = tutors;

    if (searchTerm.trim()) {
      result = result.filter(
        (tutor) =>
          tutor.skills?.some((skill) =>
            skill.toLowerCase().includes(searchTerm)
          ) ||
          tutor.subjects?.some((subject) =>
            subject.toLowerCase().includes(searchTerm)
          ) ||
          tutor.cities?.some((city) => city.toLowerCase().includes(searchTerm))
      );
    }

    if (filters.subject) {
      result = result.filter((tutor) =>
        tutor.subjects?.includes(filters.subject)
      );
    }
    if (filters.skill) {
      result = result.filter((tutor) => tutor.skills?.includes(filters.skill));
    }
    if (filters.city) {
      result = result.filter((tutor) => tutor.cities?.includes(filters.city));
    }

    if (filters.sortOrder) {
      result = [...result].sort((a, b) =>
        filters.sortOrder === "asc"
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name)
      );
    }

    return result;
  }, [searchTerm, filters, tutors]);

  return (
    <>
      <TutorMetas />
      <main id="main">
        <div className="breadcrumbs" data-aos="fade-in">
          <div className="container">
            <h1>Find a Tutor</h1>
            <p>
              Search and connect with the best tutors for your learning needs.
            </p>
          </div>
        </div>
        <div className="tutor-container">
          <div className="filter-section">
            <input
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder="Search by subject, skill, or city..."
              className="search-input"
            />
            <select
              onChange={(e) =>
                setFilters((f) => ({ ...f, subject: e.target.value }))
              }
              value={filters.subject}
            >
              <option value="">All Subjects</option>
              {subjects.map((subject, index) => (
                <option key={index} value={subject}>
                  {subject}
                </option>
              ))}
            </select>
            <select
              onChange={(e) =>
                setFilters((f) => ({ ...f, skill: e.target.value }))
              }
              value={filters.skill}
            >
              <option value="">All Skills</option>
              {skills.map((skill, index) => (
                <option key={index} value={skill}>
                  {skill}
                </option>
              ))}
            </select>
            <select
              onChange={(e) =>
                setFilters((f) => ({ ...f, city: e.target.value }))
              }
              value={filters.city}
            >
              <option value="">All Cities</option>
              {cities.map((city, index) => (
                <option key={index} value={city}>
                  {city}
                </option>
              ))}
            </select>
            <select
              onChange={(e) =>
                setFilters((f) => ({ ...f, sortOrder: e.target.value }))
              }
              value={filters.sortOrder}
            >
              <option value="">Sort by Name</option>
              <option value="asc">A to Z</option>
              <option value="desc">Z to A</option>
            </select>
          </div>

          <div className="tutor-list">
            {filteredTutors.length > 0 ? (
              <table className="tutor-table">
                <thead>
                  <tr>
                    <th>Profile</th>
                    <th>Name</th>
                    <th>Tutoring Fee</th>
                    <th>Location</th>
                    <th>Experience</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTutors.map((tutor, index) => (
                    <tr key={index}>
                      <td>
                        <img
                          src={userImg}
                          alt={tutor.name}
                          className="tutor-img"
                        />
                      </td>
                      <td>{tutor.name || "N/A"}</td>
                      <td>
                        {tutor.pricing?.amount
                          ? `${tutor.pricing.amount} Rs / ${
                              tutor.pricing.type || "hourly"
                            }`
                          : "N/A"}
                      </td>
                      <td>{tutor.cities?.join(", ") || "N/A"}</td>
                      <td>
                        {tutor.experience ? `${tutor.experience} years` : "N/A"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="not-found-tutor">No tutors found</p>
            )}
          </div>
        </div>
      </main>
    </>
  );
};

export default Tutor;
